import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { Medication, RxDispenseEvent, medicationService } from '../../services/medicationService';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface FillHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  medication: Medication;
  /** Called after a successful void so the parent can refresh the Rx chip. */
  onChanged?: () => void;
}

/**
 * Fill history for one take-home Rx — every dispense event (including voided
 * ones, kept for the audit trail), with a Void action on the live ones.
 *
 * Voiding a fill goes through medicationService.voidDispenseEvent, which the
 * backend handles transactionally: it frees the fill back up on the Rx
 * (reconciles refillsRemaining) AND reverses the auto-billed invoice line so
 * the client isn't charged for a voided fill.
 */
const FillHistoryDialog: React.FC<FillHistoryDialogProps> = ({
  open,
  onClose,
  medication,
  onChanged,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  /*
   * `RxDispenseEvent.dispensedAt` is an INSTANT — `@default(now())`, stamped by
   * the server when the fill actually left the clinic (DATE-KINDS.md §1.3). An
   * instant is *supposed* to read differently per zone, and the only zone that
   * means anything for a fill record is the CLINIC's, not whichever one the
   * viewer's laptop happens to be in. `format(new Date(dispensedAt), …)` used
   * the browser's, which was wrong for remote/relief staff and wrong near
   * midnight for everyone — including on the "Void" audit trail below.
   *
   * ⚠ The inverse of the calendar sites in this folder: do NOT route this
   * through `formatCalendarDate`, which would throw the time of day away.
   */
  const formatInstant = useFormatInstant();
  const {
    data: events = [],
    isLoading,
    refetch,
  } = useQuery(
    ['dispense-events', medication.id],
    () => medicationService.listDispenseEvents(medication.id),
    { enabled: open },
  );

  const [voidTarget, setVoidTarget] = useState<RxDispenseEvent | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  const handleConfirmVoid = async () => {
    if (!voidTarget) return;
    setVoiding(true);
    try {
      const res = await medicationService.voidDispenseEvent(
        medication.id,
        voidTarget.id,
        voidReason.trim() || undefined,
      );
      const credit =
        res.invoiceReversal?.reversed && res.invoiceReversal.amount
          ? ` — $${res.invoiceReversal.amount.toFixed(2)} removed from the invoice`
          : '';
      enqueueSnackbar(`Fill voided${credit}`, { variant: 'success' });
      setVoidTarget(null);
      setVoidReason('');
      await refetch();
      onChanged?.();
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message || 'Failed to void fill',
        { variant: 'error' },
      );
    } finally {
      setVoiding(false);
    }
  };

  const liveCount = events.filter((e) => !e.voidedAt).length;
  const totalFills = (medication.refillsAuthorized ?? 0) + 1;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Fill history — {medication.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {liveCount} of {totalFills} fills dispensed. Voiding a fill frees it
            back up on the Rx and removes its charge from a pending invoice.
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : events.length === 0 ? (
            <Alert severity="info">No fills dispensed yet for this Rx.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Dispensed by</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((e) => (
                    <TableRow key={e.id} sx={{ opacity: e.voidedAt ? 0.55 : 1 }}>
                      <TableCell data-testid="fill-dispensed-at">
                        {formatInstant(e.dispensedAt, 'MMM d, yyyy · h:mm a')}
                      </TableCell>
                      <TableCell>
                        {parseFloat(e.quantity).toString()} {e.unit}
                      </TableCell>
                      <TableCell>{e.dispensedBy?.name ?? '—'}</TableCell>
                      <TableCell align="right">
                        {e.voidedAt ? (
                          <Tooltip
                            title={e.voidReason ? `Voided: ${e.voidReason}` : 'Voided'}
                          >
                            <Chip size="small" label="Voided" />
                          </Tooltip>
                        ) : (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => setVoidTarget(e)}
                            sx={{ textTransform: 'none' }}
                          >
                            Void
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Void confirmation — captures an optional reason for the audit trail. */}
      <Dialog
        open={!!voidTarget}
        onClose={() => !voiding && setVoidTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Void this fill?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This marks the fill as voided, frees a fill back up on the Rx, and
            removes its charge from the invoice if it's still pending. The fill
            record is kept for the audit trail.
          </Typography>
          <TextField
            label="Reason (optional)"
            placeholder="e.g. dispensed in error, wrong quantity"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            disabled={voiding}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidTarget(null)} disabled={voiding}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmVoid}
            disabled={voiding}
            startIcon={
              voiding ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {voiding ? 'Voiding…' : 'Void fill'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FillHistoryDialog;
